import Category from "@mui/icons-material/Category";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InsightsIcon from "@mui/icons-material/Insights";
import LocalAtm from "@mui/icons-material/LocalAtm";
import Settings from "@mui/icons-material/Settings";
import ShopIcon from "@mui/icons-material/Shop";
import ShoppingBag from "@mui/icons-material/ShoppingBag";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

import ROUTE_URLS from "@/constants/route-urls";
import { SIDEBAR_MESSAGES as messages } from "@/messages/layout/sidebar";

interface BaseItem {
  type: "collapse" | "divider" | "title";
  key: string;
}

interface IconItem {
  icon?: React.ReactNode;
}

interface CollapsibleItem extends BaseItem, IconItem {
  type: "collapse";
  name: string;
  code: string;
  collapse?: NestedCollapseItem[];
  route?: string;
  href?: string;
  noCollapse?: boolean;
}

export interface NestedCollapseItem {
  name: string;
  key: string;
  code: string;
  route?: string;
  href?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  collapse?: NestedCollapseItem[];
}

interface DividerItem extends BaseItem {
  type: "divider";
}

interface TitleItem extends BaseItem {
  type: "title";
  title: string;
  shortTitle: string;
}

const lang = "PT-BR";

export type MenuItem = CollapsibleItem | DividerItem | TitleItem;

const routes: MenuItem[] = [
  {
    type: "collapse",
    name: messages.contract_now,
    key: "plans",
    code: "PLANS",
    route: ROUTE_URLS.plans(lang),
    icon: <ShopIcon fontSize="medium" />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: messages.dashboard,
    key: "dashboard",
    code: "DASHBOARD",
    icon: <DashboardIcon fontSize="medium" />,
    collapse: [
      {
        name: messages.sub_customer_service,
        key: "service",
        code: "SERVICE",
        collapse: [
          {
            name: messages.operational,
            key: "operational",
            code: "SERVICE",
            icon: <SpeedIcon fontSize="small" />,
            route: ROUTE_URLS.serviceDashboard(lang),
          },
          {
            name: messages.managerial,
            key: "managerial",
            code: "SERVICE",
            icon: <InsightsIcon fontSize="small" />,
            route: ROUTE_URLS.serviceManagerialDashboard(lang),
          },
        ],
      },
      {
        name: messages.sales,
        key: "sales",
        code: "SALES",
        route: ROUTE_URLS.salesDashboard(lang),
      },
    ],
  },
  {
    type: "collapse",
    name: messages.customer_service,
    key: "customerService",
    code: "CUSTOMERS",
    icon: <SupportAgentIcon fontSize="medium" />,
    collapse: [
      {
        name: messages.contacts,
        key: "contacts",
        code: "CONTACT",
        route: ROUTE_URLS.contacts(lang),
      },
      {
        name: messages.chat,
        key: "chat",
        code: "MESSAGE",
        route: ROUTE_URLS.chat(lang),
      },
      {
        name: messages.conversation_report,
        key: "conversation-report",
        code: "MESSAGE",
        route: ROUTE_URLS.conversationReport(lang),
      },
    ],
  },
  {
    type: "collapse",
    name: messages.orders,
    key: "orders",
    code: "ORDERS",
    icon: <ShoppingBag fontSize="medium" />,
    collapse: [
      {
        name: messages.sub_orders,
        key: "orders",
        code: "ORDER",
        route: ROUTE_URLS.orders(lang),
      },
    ],
  },
  {
    type: "collapse",
    name: messages.catalog,
    key: "catalog",
    code: "CATALOG",
    icon: <Category fontSize="medium" />,
    collapse: [
      {
        name: messages.products,
        key: "products",
        code: "PRODUCT",
        route: ROUTE_URLS.products(lang),
      },
      {
        name: messages.variations,
        key: "productVariations",
        code: "PRODUCTVARIATION",
        route: ROUTE_URLS.variations(lang),
      },
      {
        name: messages.categories,
        key: "productCategories",
        code: "PRODUCTCATEGORY",
        route: ROUTE_URLS.productCategories(lang),
      },
    ],
  },
  {
    type: "collapse",
    name: messages.pricing,
    key: "pricing",
    code: "PRICING",
    icon: <LocalAtm fontSize="medium" />,
    collapse: [
      {
        name: messages.campaigns,
        key: "campaigns",
        code: "CAMPAIGNS",
        route: ROUTE_URLS.campaigns(lang),
      },
      {
        name: messages.price_list,
        key: "listPrice",
        code: "LISTPRICE",
        route: ROUTE_URLS.listPrice(lang),
      },
    ],
  },
  {
    type: "collapse",
    name: messages.configurations,
    key: "settings",
    code: "SETTINGS",
    icon: <Settings fontSize="medium" />,
    collapse: [
      {
        name: messages.company_data,
        key: "company",
        code: "COMPANY",
        route: ROUTE_URLS.customer(lang),
      },
      {
        name: messages.coverage_areas,
        key: "coverage-area",
        code: "FREIGHT_ZONES",
        route: ROUTE_URLS.coverageArea(lang),
      },
      {
        name: messages.service_queues,
        key: "service-queue",
        code: "CUSTOMERSETTINGS",
        route: ROUTE_URLS.serviceQueue(lang),
      },
      {
        name: messages.subjects,
        key: "subjects",
        code: "CUSTOMERSETTINGS",
        route: ROUTE_URLS.subjects(lang),
      },
      {
        name: messages.platforms,
        key: "platforms",
        code: "PLATFORM",
        route: ROUTE_URLS.platform(lang),
      },
      {
        name: messages.message_templates,
        key: "message-templates",
        code: "CUSTOMERTEMPLATE",
        route: ROUTE_URLS.messageTemplates(lang),
      },
      {
        name: messages.integrations,
        key: "integrations",
        code: "PLATFORM",
        route: ROUTE_URLS.integrations(lang),
      },
      {
        name: messages.workflow,
        key: "workflow",
        code: "WORKFLOW",
        route: ROUTE_URLS.workflow(lang),
      },
      {
        name: messages.users,
        key: "users",
        code: "USER",
        route: ROUTE_URLS.users(lang),
      },
    ],
  },
];

export default routes;
